// PRD.280 — Voice-assisted prescription dictation hook.
//
// Drives MediaRecorder for a single ephemeral dictation, transcribes+translates
// to English (reusing the PRD.279 voice-to-text boundary), then calls the
// `pextractMedicationsFromTranscript` Cloud Function and hands the extracted
// medications to the caller's `applyMedications` for client-side inventory
// matching + draft insertion.
//
// Structure mirrors `useEliaVoiceDictation`: pure imperative refs, a generation
// counter to reject stale callbacks, a captured context key so a completion can
// never write into the wrong patient/visit, and ONE idempotent cleanup.
//
// Privacy (PRD.280 §Voice Capture): audio lives only as in-memory chunks for the
// active operation — no blob retained, no object URL, nothing persisted, and no
// transcript / patient id ever logged. React NEVER calls the provider directly.
//
// Reliability (PRD.280 §Duplicate Handling): a single completed recording is
// applied AT MOST ONCE. The generation guard + a per-session `appliedRef` make
// duplicate clicks, promise re-resolution, and re-renders safe.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';
import type { ExtractedMedication } from '@helios/data-access';

import { MAX_RECORDING_DURATION_S, transcribeAudioBlob } from '../voice/vtt-support';
import { blobToBase64, isRecordingSupported, pickSupportedMimeType } from '../voice/vtt-recording';

const EXTRACT_URL = import.meta.env.VITE_MEDICATION_EXTRACT_URL;
const VTT_URL = import.meta.env.VITE_VOICE_TO_TEXT_URL;

/** Voice dictation normalizes speech to English before extraction. */
const DICTATION_MODE = 'translation' as const;

export type VoicePhase =
  | 'idle'
  | 'recording'
  | 'transcribing' // "Transcribing and translating…"
  | 'extracting' // "Extracting medications…"
  | 'adding' // "Adding prescription items…"
  | 'error';

/** Context captured at record-start; the request also carries it for scope/log. */
export interface VoicePrescriptionContext {
  orgId: string;
  patientId: string;
  visitId?: string | null;
  encounterId?: string | null;
}

export interface UseVoicePrescriptionArgs {
  /** Server context — also used to build the stale-guard key. */
  context: VoicePrescriptionContext;
  /** Feature+permission gate resolved by the caller; false hides/disables mic. */
  permitted: boolean;
  /**
   * Apply extracted medications (match inventory + insert editable draft rows).
   * Returns the number of items added. Invoked AT MOST ONCE per recording,
   * only after the generation + context guards pass.
   */
  applyMedications: (
    medications: ExtractedMedication[],
    meta: { requestId?: string },
  ) => Promise<number>;
}

export interface UseVoicePrescriptionReturn {
  phase: VoicePhase;
  error: string | null;
  /** True when the last error is safe to retry (network/timeout/429/5xx). */
  retryable: boolean;
  elapsed: number;
  maxDuration: number;
  /** Browser can record audio. */
  supported: boolean;
  /** Mic control should be shown (permitted + supported + endpoints configured). */
  available: boolean;
  /** Mic may begin a new recording right now. */
  canStart: boolean;
  /** Count from the most recent successful apply (for the success toast). */
  lastAddedCount: number | null;
  start: () => void;
  stop: () => void;
  cancel: () => void;
  dismissError: () => void;
}

function contextKeyOf(c: VoicePrescriptionContext): string {
  return `${c.orgId}:${c.patientId}:${c.visitId ?? ''}:${c.encounterId ?? ''}`;
}

export function useVoicePrescription(args: UseVoicePrescriptionArgs): UseVoicePrescriptionReturn {
  const [phase, setPhase] = useState<VoicePhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [lastAddedCount, setLastAddedCount] = useState<number | null>(null);

  const supported = useState(() => isRecordingSupported())[0];
  const configured = Boolean(VTT_URL) && Boolean(EXTRACT_URL);
  const available = args.permitted && supported && configured;

  // ── Imperative machinery (never triggers renders) ──
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');
  const generationRef = useRef(0);
  const recordStartKeyRef = useRef<string>('');
  /** Guards the single apply per recording session (idempotency). */
  const appliedRef = useRef(false);
  const stopRef = useRef<() => void>(() => {});

  // Latest props readable inside async callbacks without widening deps.
  const contextRef = useRef(args.context);
  contextRef.current = args.context;
  const applyRef = useRef(args.applyMedications);
  applyRef.current = args.applyMedications;

  const currentContextKey = contextKeyOf(args.context);

  const isStale = useCallback((gen: number): boolean => gen !== generationRef.current, []);

  const releaseResources = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setElapsed(0);
  }, []);

  /** Cleanup AND invalidate any in-flight async result (bumps the generation). */
  const invalidate = useCallback(() => {
    generationRef.current += 1;
    releaseResources();
  }, [releaseResources]);

  const fail = useCallback((message: string, canRetry: boolean) => {
    setError(message);
    setRetryable(canRetry);
    setPhase('error');
  }, []);

  /** POST the translated transcript to the medication-extraction function. */
  const extractMedications = useCallback(
    async (
      text: string,
      signal: AbortSignal,
    ): Promise<{ medications: ExtractedMedication[]; requestId?: string } | 'error'> => {
      const ctx = contextRef.current;
      try {
        const token = await getIdToken();
        const res = await fetch(EXTRACT_URL as string, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            text,
            orgId: ctx.orgId,
            patientId: ctx.patientId,
            ...(ctx.visitId ? { visitId: ctx.visitId } : {}),
            ...(ctx.encounterId ? { encounterId: ctx.encounterId } : {}),
          }),
          signal,
        });
        const body: unknown = await res.json().catch(() => null);
        if (!res.ok) {
          const asObj = (typeof body === 'object' && body !== null ? body : {}) as Record<
            string,
            unknown
          >;
          fail('Could not extract prescription items.', asObj.retryable === true);
          return 'error';
        }
        const obj = (body ?? {}) as {
          medications?: ExtractedMedication[];
          requestId?: string;
        };
        return { medications: obj.medications ?? [], requestId: obj.requestId };
      } catch (err) {
        const isAbort =
          (err instanceof Error && (err.name === 'AbortError' || err.name === 'TimeoutError')) ||
          signal.aborted;
        if (isAbort) return 'error'; // silent — our own teardown
        fail('Could not reach the extraction service.', true);
        return 'error';
      }
    },
    [fail],
  );

  const runPipeline = useCallback(
    async (blob: Blob, mime: string, gen: number, capturedKey: string) => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        // 1) Transcribe + translate to English.
        setPhase('transcribing');
        const audioBase64 = await blobToBase64(blob);
        if (isStale(gen)) return;
        const result = await transcribeAudioBlob(
          { audioBase64, mimeType: mime, mode: DICTATION_MODE, signal: controller.signal },
          { endpointUrl: VTT_URL, getToken: getIdToken },
        );
        if (isStale(gen) || capturedKey !== contextKeyOf(contextRef.current)) return;
        const text = (result.text ?? '').trim();
        if (!text) {
          fail('No speech was detected. Try again.', true);
          return;
        }

        // 2) Backend medication extraction.
        setPhase('extracting');
        const extracted = await extractMedications(text, controller.signal);
        if (isStale(gen) || capturedKey !== contextKeyOf(contextRef.current)) return;
        if (extracted === 'error') return; // fail() already set state
        abortRef.current = null;

        if (extracted.medications.length === 0) {
          fail('No medications were detected. Try again.', true);
          return;
        }

        // 3) Apply — match inventory + insert draft rows. AT MOST ONCE.
        if (appliedRef.current) return;
        appliedRef.current = true;
        setPhase('adding');
        const added = await applyRef.current(extracted.medications, {
          requestId: extracted.requestId,
        });
        if (isStale(gen)) return;
        setLastAddedCount(added);
        setError(null);
        setRetryable(false);
        setPhase('idle');
      } catch (err) {
        abortRef.current = null;
        if (isStale(gen)) return; // our own abort — not user-facing
        const isAbort = err instanceof Error && err.name === 'AbortError';
        if (isAbort) return;
        fail('Could not extract prescription items.', true);
      }
    },
    [extractMedications, fail, isStale],
  );

  const start = useCallback(() => {
    if (phase !== 'idle' && phase !== 'error') return; // never interrupt a live op
    if (!available) return;
    const mime = pickSupportedMimeType();
    if (!mime) {
      fail('Voice recording is not supported in this browser.', false);
      return;
    }
    mimeTypeRef.current = mime;

    const gen = (generationRef.current += 1);
    appliedRef.current = false;
    const capturedKey = contextKeyOf(contextRef.current);
    recordStartKeyRef.current = capturedKey;
    setError(null);
    setRetryable(false);
    setLastAddedCount(null);
    setElapsed(0);
    setPhase('recording');
    chunksRef.current = [];

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (isStale(gen)) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const recorder = new MediaRecorder(stream, { mimeType: mime });
        mediaRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.start();
        timerRef.current = setInterval(() => setElapsed((p) => p + 1), 1000);
        autoStopRef.current = setTimeout(() => stopRef.current(), MAX_RECORDING_DURATION_S * 1000);
      })
      .catch(() => {
        if (isStale(gen)) return;
        fail('Microphone permission was denied.', false);
        releaseResources();
      });
  }, [phase, available, fail, isStale, releaseResources]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') {
      // Nothing captured (e.g. Stop during the permission prompt) — no backend call.
      invalidate();
      setPhase('idle');
      return;
    }

    const gen = generationRef.current;
    const capturedKey = recordStartKeyRef.current;
    const mime = mimeTypeRef.current;

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime });
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      mediaRecorderRef.current = null;
      chunksRef.current = [];

      if (isStale(gen)) return; // Cancel/context-change/unmount already handled UI.
      if (blob.size === 0) {
        fail('No speech was detected. Try again.', true);
        return;
      }
      void runPipeline(blob, mime, gen, capturedKey);
    };
    recorder.stop();
  }, [invalidate, isStale, runPipeline, fail]);

  const cancel = useCallback(() => {
    invalidate();
    setPhase('idle');
    setError(null);
    setRetryable(false);
  }, [invalidate]);

  const dismissError = useCallback(() => {
    setError(null);
    setRetryable(false);
    if (phase === 'error') setPhase('idle');
  }, [phase]);

  stopRef.current = stop;

  // ── Context change → cancel any live operation (stale-context guard) ──
  const prevContextKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevContextKeyRef.current;
    prevContextKeyRef.current = currentContextKey;
    if (prev === undefined || prev === currentContextKey) return;
    if (phase !== 'idle' && phase !== 'error') {
      invalidate();
      setPhase('idle');
      setError(null);
    }
  }, [currentContextKey, phase, invalidate]);

  // ── Unmount cleanup ──
  useEffect(() => () => invalidate(), [invalidate]);

  const canStart = useMemo(
    () => available && (phase === 'idle' || phase === 'error'),
    [available, phase],
  );

  return {
    phase,
    error,
    retryable,
    elapsed,
    maxDuration: MAX_RECORDING_DURATION_S,
    supported,
    available,
    canStart,
    lastAddedCount,
    start,
    stop,
    cancel,
    dismissError,
  };
}
